import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Trash2, Users } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useRosters,
  useShifts,
  useShiftActions,
  useBiometricEmployees,
} from '../hooks/useBiometricAdmin';

const dateOnly = (value?: string | null) => (value ? value.slice(0, 10) : '—');

/** Assign shifts to employees, individually or in bulk by department. */
export function RosterEditor() {
  const { data: rosters, isLoading } = useRosters();
  const { data: shifts } = useShifts();
  const { data: employees } = useBiometricEmployees();
  const { bulkAssign, deleteRoster } = useShiftActions();

  const [form, setForm] = useState({
    shiftId: '',
    from: new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
    to: '',
    department: '',
  });
  const [selected, setSelected] = useState<string[]>([]);

  const activeShifts = (shifts ?? []).filter(s => s.is_active !== false);
  const departments = [...new Set((employees ?? []).map(e => e.department).filter(Boolean))] as string[];

  const inDepartment = form.department
    ? (employees ?? []).filter(e => e.department === form.department)
    : (employees ?? []);

  const assign = async () => {
    if (!form.shiftId) {
      toast.error('Pick a shift.');
      return;
    }
    const userIds = selected.length ? selected : inDepartment.map(e => e.id);
    if (!userIds.length) {
      toast.error('No employees selected.');
      return;
    }
    try {
      const result = await bulkAssign.mutateAsync({
        userIds,
        shiftId: form.shiftId,
        from: form.from,
        to: form.to || undefined,
      });
      toast.success(`Assigned to ${result.assigned} employee(s).`);
      setSelected([]);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4">
      <Card className="space-y-3 p-4">
        <h2 className="text-lg font-semibold text-on-surface">Assign a shift</h2>
        <p className="text-sm text-on-surface-variant">
          A roster is effective from a date onwards. Leave the end date blank for an open-ended assignment;
          the newest assignment covering a date wins.
        </p>

        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Shift">
            <select
              className="h-10 w-full rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
              value={form.shiftId}
              onChange={e => setForm(f => ({ ...f, shiftId: e.target.value }))}
            >
              <option value="">Select…</option>
              {activeShifts.map(s => (
                <option key={s._id} value={s._id}>
                  {s.name} ({s.start_time}–{s.end_time})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Effective from">
            <Input type="date" value={form.from} onChange={e => setForm(f => ({ ...f, from: e.target.value }))} />
          </Field>
          <Field label="Until (optional)">
            <Input type="date" value={form.to} onChange={e => setForm(f => ({ ...f, to: e.target.value }))} />
          </Field>
          <Field label="Department filter">
            <select
              className="h-10 w-full rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
              value={form.department}
              onChange={e => {
                setForm(f => ({ ...f, department: e.target.value }));
                setSelected([]);
              }}
            >
              <option value="">All departments</option>
              {departments.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="max-h-56 overflow-y-auto rounded-md border border-outline-variant p-2">
          <label className="mb-1 flex items-center gap-2 border-b border-outline-variant pb-1 text-sm font-medium text-on-surface">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-outline-variant"
              checked={selected.length > 0 && selected.length === inDepartment.length}
              onChange={e => setSelected(e.target.checked ? inDepartment.map(x => x.id) : [])}
            />
            Select all ({inDepartment.length})
          </label>
          <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {inDepartment.map(emp => (
              <label key={emp.id} className="flex items-center gap-2 text-sm text-on-surface">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-outline-variant"
                  checked={selected.includes(emp.id)}
                  onChange={e =>
                    setSelected(prev =>
                      e.target.checked ? [...prev, emp.id] : prev.filter(id => id !== emp.id),
                    )
                  }
                />
                <span className="truncate">{emp.display_name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={assign} disabled={bulkAssign.isPending}>
            <Users className="mr-1 h-4 w-4" />
            Assign to {selected.length || inDepartment.length} employee(s)
          </Button>
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="mb-3 font-semibold text-on-surface">Current rosters</h3>
        {isLoading ? (
          <div className="flex justify-center p-8"><Spinner /></div>
        ) : !rosters?.length ? (
          <p className="py-6 text-center text-sm text-on-surface-variant">
            No rosters assigned — everyone falls back to the first active shift.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="border-b border-outline-variant text-left text-xs uppercase tracking-wide text-on-surface-variant">
                  <th className="px-2 py-2">Employee</th>
                  <th className="px-2 py-2">Shift</th>
                  <th className="px-2 py-2">From</th>
                  <th className="px-2 py-2">To</th>
                  <th className="px-2 py-2 text-right">—</th>
                </tr>
              </thead>
              <tbody>
                {rosters.map(roster => (
                  <tr key={roster._id} className="border-b border-outline-variant/50">
                    <td className="px-2 py-1.5 text-on-surface">{roster.user_id?.display_name ?? '—'}</td>
                    <td className="px-2 py-1.5 text-on-surface-variant">
                      {roster.shift_id?.name ?? '—'}
                      {roster.shift_id?.start_time && (
                        <span className="ml-1 font-mono text-xs">
                          {roster.shift_id.start_time}–{roster.shift_id.end_time}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">{dateOnly(roster.effective_from)}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">{dateOnly(roster.effective_to) || 'open'}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        type="button"
                        className="rounded p-1 text-danger hover:bg-danger/10"
                        onClick={async () => {
                          if (!window.confirm('Delete this roster assignment?')) return;
                          try {
                            await deleteRoster.mutateAsync(roster._id);
                            toast.success('Roster removed.');
                          } catch (err) {
                            toast.error(getApiErrorMessage(err));
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-on-surface">{label}</label>
      {children}
    </div>
  );
}
